import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiUser, FiFileText, FiBell } from 'react-icons/fi';
import { useAuth } from '../../../store/auth';
import { ShareholderService } from '../../../services/ShareholderService';
import { AuthService } from '../../../services/AuthService';
import { useApi } from '../../../services/hooks/apiHook';
import { AxiosInstance } from 'axios';

export const DirectorHome: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const api: AxiosInstance = useApi();

  const [isLoading, setIsLoading] = useState(true);
  const [shareholderData, setShareholderData] = useState({
    name: '',
    email: '',
    role: '',
    company: '',
    profileComplete: 0,
    documentsSubmitted: 0
  });
  const [notifications, setNotifications] = useState<any[]>([]);

  const lastFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const initialLanguageSyncRef = useRef(true);

  // Memoize config to prevent unnecessary re-renders
  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      }
    }),
    [token, i18n.language]
  );

  const fetchData = useCallback(async () => {
    if (!token || isFetchingRef.current) {
      return;
    }

    const fetchKey = `${token}|${i18n.language}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;
    isFetchingRef.current = true;

    try {
      setIsLoading(true);

      // Fetch shareholder data, documents, companies and notifications
      const [meData, addressData, passportData, documentsData, companiesData, notificationsData] = await Promise.all([
        ShareholderService.getMe(api, config),
        ShareholderService.getMyAddress(api, config),
        ShareholderService.getMyPassports(api, config),
        ShareholderService.getMyDocuments(api, config),
        AuthService.allCompanies(api, config),
        AuthService.getNotificationsMe(api, config).catch(() => ({ data: [] }))
      ]);

      const shareholder = meData.data;
      
      // Calculate profile completion percentage
      let completedFields = 0;
      const totalFields = 7;
      
      if (shareholder.name) completedFields++;
      if (shareholder.email) completedFields++;
      if (shareholder.date_of_birth) completedFields++;
      if (shareholder.nationality) completedFields++;
      if (addressData.data.length > 0) completedFields++;
      if (passportData.data.length > 0) completedFields++;
      if (shareholder.document_number) completedFields++;
      
      const profileComplete = Math.round((completedFields / totalFields) * 100);

      // Get company name from API response
      const companyName: string = companiesData.data?.[0]?.name || 
        companiesData.data?.[0]?.ibc_primary || 
        (t('labels.lblNoCompanyName') as string) || 
        (t('others.othCompany') as string) || 
        '';

      // Count all types of documents: general documents + passports + addresses
      const totalDocuments = 
        (documentsData.data?.length || 0) + 
        (passportData.data?.length || 0) + 
        (addressData.data?.length || 0);

      // Get notifications (limit to 3 most recent)
      const recentNotifications = Array.isArray(notificationsData) 
        ? notificationsData.slice(0, 3) 
        : [];

      // Determine role based on is_director field from API
      const roleLabel: string = shareholder.is_director 
        ? ((t('labels.lblDirector') as string) || 'Director')
        : ((t('labels.lblShareholder') as string) || 'Shareholder');

      setShareholderData({
        name: shareholder.name,
        email: shareholder.email,
        role: roleLabel,
        company: companyName,
        profileComplete,
        documentsSubmitted: totalDocuments
      });
      
      setNotifications(recentNotifications);
    } catch (error) {
      console.error('Error fetching shareholder data:', error);
      lastFetchKeyRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [token, api, config, i18n.language, t]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchData();
      return;
    }

    const timeout = setTimeout(() => {
      void fetchData();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, i18n.language, fetchData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('labels.lblLoading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col border-b-2 pb-3">
        <h2 className="text-xl font-bold text-[#0C1FAA]">
          {t('titles.ttlHello') || 'Hello'}, {shareholderData.name}
        </h2>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 bg-gradient-to-r from-blue-50 to-purple-50 rounded-md px-4 py-2 border-l-4 border-[#0C1FAA]">
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-gray-500">{t('others.othCompany')}:</span>
              <h3 className="text-lg font-bold text-[#0C1FAA]">
                {shareholderData.company}
              </h3>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-sm text-gray-600">{shareholderData.role}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de informações */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {/* Card de Perfil */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-full">
                <FiUser className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('labels.lblMyProfile')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('labels.lblViewAndEdit')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-4 flex-grow">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">{t('labels.lblProfileComplete')}</span>
              <span className="font-semibold text-gray-800">{shareholderData.profileComplete}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all" 
                style={{ width: `${shareholderData.profileComplete}%` }}
              ></div>
            </div>
          </div>

          <Link
            to="/dashboard/profile"
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors mt-auto"
          >
            {t('labels.lblViewProfile')}
          </Link>
        </div>

        {/* Card de Documentos */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-full">
                <FiFileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('labels.lblMyDocuments')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('labels.lblDocumentStatus')}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('labels.lblSubmitted')}</span>
                <span className="text-3xl font-bold text-green-600">
                  {shareholderData.documentsSubmitted}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {t('labels.lblViewYourDocuments')}
              </p>
            </div>
          </div>

          <Link
            to="/dashboard/documents"
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors mt-auto"
          >
            {t('labels.lblViewYourDocuments')}
          </Link>
        </div>

        {/* Card de Notificações */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-full">
                <FiBell className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('labels.lblNotification')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('labels.lblRecentUpdates') || 'Recent updates'}
                </p>
              </div>
            </div>
            {notifications.length > 0 && (
              <span className="bg-purple-100 text-purple-600 text-xs font-semibold px-2 py-1 rounded-full">
                {notifications.filter((n: any) => !n.read_at).length}
              </span>
            )}
          </div>

          <div className="mb-4 flex-grow max-h-[140px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">
                  {t('labels.lblNoNotifications')}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('labels.lblAllCaughtUp') || 'You\'re all caught up!'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notification: any) => (
                  <div 
                    key={notification.id}
                    className={`p-2 rounded-lg border text-xs ${
                      notification.read_at 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-purple-50 border-purple-200'
                    }`}
                  >
                    <p className="text-gray-700 line-clamp-2">
                      {notification.data}
                    </p>
                    <p className="text-gray-400 text-[10px] mt-1">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            disabled
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-md cursor-not-allowed mt-auto"
          >
            {t('labels.lblViewAll') || 'View All'}
          </button>
        </div>
      </div>

      {/* Informações adicionais */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FiUser className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {t('labels.lblAboutDirectorDashboard')}
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {t('labels.lblDirectorDashboardDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
